import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Tooltip,
  Link
} from '@mui/material';
import {
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
  Add as AddIcon
} from '@mui/icons-material';

export default function GoalDrawer({ 
  open, 
  goal, 
  onClose, 
  onUpdate, 
  onDelete,
  onAddActionItem
}) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [localGoal, setLocalGoal] = useState(null);

  // Initialize localGoal when goal prop changes
  useEffect(() => {
    if (goal) {
      setLocalGoal(goal);
    }
  }, [goal]);

  // Don't render drawer if no goal is selected
  if (!localGoal) {
    return null;
  }

  const handleScoreChange = (field, value) => {
    const updatedGoal = {
      ...localGoal,
      [field]: value
    };
    
    // Calculate total score as sum instead of average
    const scores = [
      updatedGoal.thrillingScore || 0,
      updatedGoal.importantScore || 0,
      updatedGoal.dauntingScore || 0
    ];
    updatedGoal.score = scores.reduce((a, b) => a + b, 0);
    
    setLocalGoal(updatedGoal);
    onUpdate(updatedGoal);
  };

  const handleDelete = () => {
    onDelete(goal.id);
    setIsDeleteDialogOpen(false);
    onClose();
  };

  const scoreOptions = Array.from({ length: 13 }, (_, i) => (i * 0.5) + 1);

  const handleAddActionItem = () => {
    onAddActionItem(goal.id);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: '50%' }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', pl: 3, pr: 1 }}>
        {/* Fixed Header */}
        <Box sx={{ 
          p: 3, 
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              GOAL
            </Typography>
            <Box>
              <IconButton onClick={() => setIsDeleteDialogOpen(true)} sx={{ mr: 1, color: 'red' }}>
                <DeleteIcon />
              </IconButton>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          <TextField
            fullWidth
            value={localGoal.goal}
            onChange={(e) => handleScoreChange('goal', e.target.value)}
            variant="standard"
            multiline // Enable multiline behavior
            maxRows={4} // Set the maximum number of lines (optional)
            sx={{ 
              '& .MuiInputBase-input': {
                fontSize: '24px',
                fontWeight: 500,
                lineHeight: '1.5', // Optional for better readability
                whiteSpace: 'pre-wrap', // Ensures text wraps properly
              }
            }}
          />
        </Box>

        {/* Scrollable Content */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {/* Thrilling Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">
                Why is this goal Thrilling?
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 1 }}>Score</Typography>
                <Select
                  value={localGoal.thrillingScore || 0}
                  onChange={(e) => handleScoreChange('thrillingScore', e.target.value)}
                  size="small"
                  sx={{ width: 80 }}
                >
                  {scoreOptions.map(score => (
                    <MenuItem key={score} value={score}>{score.toFixed(1)}</MenuItem>
                  ))}
                </Select>
                <Tooltip title="Learn more about scoring">
                  <IconButton size="small" onClick={() => setIsInfoDialogOpen(true)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={localGoal.thrillingReason || ''}
              onChange={(e) => handleScoreChange('thrillingReason', e.target.value)}
              placeholder="What makes this goal exciting and motivating?"
            />
          </Box>

          {/* Important Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">
                Why is this goal Important?
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 1 }}>Score</Typography>
                <Select
                  value={localGoal.importantScore || 0}
                  onChange={(e) => handleScoreChange('importantScore', e.target.value)}
                  size="small"
                  sx={{ width: 80 }}
                >
                  {scoreOptions.map(score => (
                    <MenuItem key={score} value={score}>{score.toFixed(1)}</MenuItem>
                  ))}
                </Select>
                <Tooltip title="Learn more about scoring">
                  <IconButton size="small" onClick={() => setIsInfoDialogOpen(true)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={localGoal.importantReason || ''}
              onChange={(e) => handleScoreChange('importantReason', e.target.value)}
              placeholder="Why does this goal matter to you and others?"
            />
          </Box>

          {/* Daunting Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">
                Why is this goal Daunting?
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{ mr: 1 }}>Score</Typography>
                <Select
                  value={localGoal.dauntingScore || 0}
                  onChange={(e) => handleScoreChange('dauntingScore', e.target.value)}
                  size="small"
                  sx={{ width: 80 }}
                >
                  {scoreOptions.map(score => (
                    <MenuItem key={score} value={score}>{score.toFixed(1)}</MenuItem>
                  ))}
                </Select>
                <Tooltip title="Learn more about scoring">
                  <IconButton size="small" onClick={() => setIsInfoDialogOpen(true)}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={localGoal.dauntingReason || ''}
              onChange={(e) => handleScoreChange('dauntingReason', e.target.value)}
              placeholder="What makes this goal challenging to achieve?"
            />
          </Box>

          {/* Total Score */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            mb: 4,
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 2
          }}>
            <Typography sx={{ mr: 2 }}>Total</Typography>
            <Typography variant="h6">{localGoal.score?.toFixed(1) || '0.0'}</Typography>
          </Box>

          {/* Action Items Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 2 }}>
              <Typography variant="h6">
                {(localGoal?.actionItems?.length || 0)} Action items for this goal
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddActionItem}
                sx={{
                  textTransform: 'none',
                  ml: 2,
                  borderRadius: '20px',
                  color: 'text.secondary',
                  borderColor: 'grey.300',
                  '&:hover': {
                    borderColor: 'primary.main',
                    color: 'primary.main'
                  }
                }}
              >
                Add Action item
              </Button>
            </Box>
            
          </Box>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Delete Goal?</DialogTitle>
        <DialogContent>
          This will permanently delete the goal and all its associated action items. This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog open={isInfoDialogOpen} onClose={() => setIsInfoDialogOpen(false)}>
        <DialogTitle>About Ambitious Goals</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            Ambitious goals should be:
          </Typography>
          <Typography paragraph>
            • Thrilling: They should excite and motivate you
          </Typography>
          <Typography paragraph>
            • Important: They should matter to you and others
          </Typography>
          <Typography paragraph>
            • Daunting: They should push you out of your comfort zone
          </Typography>
          <Typography>
            Rate each aspect from 1 to 7, with 7 being the highest. The total score helps gauge the ambition level of your goal.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInfoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
